import { BaseData, BaseUUIDData } from "@/types/base";
import { BasePaginationQueryParams } from "@/types/pagination";
import { DownloadResponse } from "@/services/downloadPollingService";
import { BaseUser } from "@/views/profiles/types/user";
import { BaseSurvey } from "@/redux/survey/types";

type SurveyViewTags = "SurveyView" | "SurveyViews";
type DataBlockReferencesTags = "DataBlockReferences";
export type Tags = SurveyViewTags | DataBlockReferencesTags;

export type TagsType =
  | { type: "SurveyView" }
  | { type: "SurveyView"; id: string }
  | { type: "SurveyViews" }
  | { type: "DataBlockReferences" };

export interface SurveyViewQueryParams extends BasePaginationQueryParams {
  title?: string;
  survey?: number;
  dataFormat?: string;
  distributionFormat?: string;
  organization?: number;
}

export enum SurveyViewDataFormat {
  RAW = "RAW",
  DESCRIPTIVE = "DESCRIPTIVE",
}

export enum SurveyViewDistributionFormat {
  DIGITAL = "DIGITAL",
  PRINT = "PRINT",
}

export enum SurveyViewColorScheme {
  DEFAULT = "DEFAULT",
  GRAY_SCALE = "GRAY_SCALE",
}

export enum SurveyViewPublicationDestination {
  EXTERNAL = "EXTERNAL",
  IN_APP = "IN_APP",
  WEBSITE = "WEBSITE",
}

export interface SurveyViewConfig extends BaseData {
  surveyView: number;
  colorScheme: SurveyViewColorScheme;
  dataFormat: SurveyViewDataFormat;
  distributionFormat: SurveyViewDistributionFormat;
  publicationDestination: SurveyViewPublicationDestination;
  includeFrontCover: boolean;
  includeBackCover: boolean;
  includeTableOfContent: boolean;
  includePrmePage: boolean;
  includeSdgPage: boolean;
  includeOrganizationSummary: boolean;
  includeContributors: boolean;
  includeDividers: boolean;
  coverPageBackgroundColor: string;
}

export interface SurveyViewEntity {
  id: number;
  sharingRights: string;
}

export interface SurveyViewAnswer extends BaseData {
  answer: number;
  question: number;
  blocks: SurveyViewEntity[];
  narratives: SurveyViewEntity[];
}

export interface BaseSurveyView extends BaseUUIDData {
  title: string;
  description: string;
  survey: number;
  sharingRights: string;
  views: number;
  config: SurveyViewConfig;
  version: number;
  createdBy: BaseUser;
}

export interface BaseOrganization {
  name: string;
}

export interface SurveyView extends BaseSurveyView {
  organization: BaseOrganization;
  answers: SurveyViewAnswer[];
  createdAt: string;
  createdBy: BaseUser;
}

export interface SurveyViewWithSurvey extends Omit<BaseSurveyView, "survey"> {
  survey: BaseSurvey;
}

export interface SurveyViewPreview {
  htmlData: string;
}

export type SurveyViewDownload = DownloadResponse;

export interface SurveyViewAnswerData {
  answer: number;
  question: number;
  blocks: { id: number }[];
}

export interface SurveyViewConfigData {
  includeFrontCover: boolean;
  includeBackCover: boolean;
  includeTableOfContent: boolean;
  includePrmePage: boolean;
  includeSdgPage: boolean;
  includeOrganizationSummary: boolean;
  includeContributors: boolean;
  includeDividers: boolean;
  publicationDestination: string;
  coverPageBackgroundColor: string;
  colorScheme: SurveyViewColorScheme;
  dataFormat: keyof typeof SurveyViewDataFormat;
  distributionFormat: SurveyViewDistributionFormat;
}

export interface SurveyViewData {
  survey: number;
  version: number;
  title: string;
  description?: string;
  config: SurveyViewConfigData;
  answers: SurveyViewAnswerData[];
}
